import { useCallback, useEffect, useRef, type PointerEventHandler } from "react";

type LongPressHandlers = {
  onPointerDown: PointerEventHandler<HTMLElement>;
  onPointerMove: PointerEventHandler<HTMLElement>;
  onPointerUp: PointerEventHandler<HTMLElement>;
  onPointerCancel: PointerEventHandler<HTMLElement>;
  onContextMenu: PointerEventHandler<HTMLElement>;
};

const LONG_PRESS_DELAY_MS = 420;
const MOVE_TOLERANCE_PX = 10;

export const useLongPressAction = () => {
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const cancelLongPress = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  useEffect(() => cancelLongPress, [cancelLongPress]);

  return useCallback(
    (onLongPress: () => void): LongPressHandlers => ({
      onPointerDown: (event) => {
        if (event.pointerType === "mouse") return;
        cancelLongPress();
        startRef.current = { x: event.clientX, y: event.clientY };
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          startRef.current = null;
          onLongPress();
        }, LONG_PRESS_DELAY_MS);
      },
      onPointerMove: (event) => {
        if (!startRef.current) return;
        const movedX = Math.abs(event.clientX - startRef.current.x);
        const movedY = Math.abs(event.clientY - startRef.current.y);
        if (movedX > MOVE_TOLERANCE_PX || movedY > MOVE_TOLERANCE_PX) {
          cancelLongPress();
        }
      },
      onPointerUp: cancelLongPress,
      onPointerCancel: cancelLongPress,
      onContextMenu: (event) => {
        event.preventDefault();
        cancelLongPress();
        onLongPress();
      },
    }),
    [cancelLongPress],
  );
};
